/**
 * AwarenessFrontend — Client-Side Awareness with rAF Throttling
 *
 * sl-lv2w.9: Reduces WebSocket message rate for awareness updates by
 * batching outgoing updates to max 1x per animation frame (16ms / 60fps).
 *
 * Architecture:
 * - Outgoing: Local awareness changes accumulate changed client IDs in a Set.
 *   A single rAF callback encodes ALL accumulated changes into one update.
 * - Incoming: Remote awareness updates are applied via rAF for smooth rendering.
 * - Local cursor: Zero lag — Y.js applies local state immediately in-memory.
 *   Only the network send is throttled.
 *
 * SOLID:
 * - S: Single Responsibility — Only handles awareness frontend ↔ sync bridge
 * - D: Dependency Inversion — Depends on AwarenessSync abstraction
 */
import { nanoid } from 'nanoid';
import {
  applyAwarenessUpdate,
  type Awareness,
  encodeAwarenessUpdate,
} from 'y-protocols/awareness';

import type { AwarenessRecord } from '../storage/awareness';
import type { AwarenessSync } from '../sync/awareness';
import {
  acknowledgeCanvasDragBases,
  decodeCanvasDragAwareness,
  encodeCanvasDragAwareness,
} from './canvas-drag-awareness';
import { trackHiddenClients } from './hidden-awareness';

type AwarenessChanges = Record<'added' | 'updated' | 'removed', number[]>;

/**
 * Schedule a callback for the next animation frame. A hidden document renders
 * no frames, so there the callback runs as a task; a frame still pending when
 * the document gets hidden is moved to a task as well. Outside a browser
 * (Node/test) the callback always runs as a task. Returns a cancel function.
 */
function scheduleFrame(callback: () => void): () => void {
  if (typeof requestAnimationFrame !== 'function') {
    const id = setTimeout(callback, 0);
    return () => clearTimeout(id);
  }
  if (typeof document === 'undefined') {
    const id = requestAnimationFrame(callback);
    return () => cancelAnimationFrame(id);
  }
  let frame: number | undefined;
  let task: ReturnType<typeof setTimeout> | undefined;
  let settled = false;
  const settle = () => {
    settled = true;
    if (frame !== undefined) cancelAnimationFrame(frame);
    if (task !== undefined) clearTimeout(task);
    document.removeEventListener('visibilitychange', runAsTaskWhenHidden);
  };
  const run = () => {
    if (settled) return;
    settle();
    callback();
  };
  function runAsTaskWhenHidden() {
    if (document.visibilityState !== 'hidden' || task !== undefined) return;
    task = setTimeout(run, 0);
  }
  document.addEventListener('visibilitychange', runAsTaskWhenHidden);
  if (document.visibilityState === 'hidden') task = setTimeout(run, 0);
  else frame = requestAnimationFrame(run);
  return settle;
}

export class AwarenessFrontend {
  constructor(private readonly sync: AwarenessSync) {}

  connectAwareness(awareness: Awareness) {
    const uniqueId = nanoid();
    // A new subscription must establish its receipts on this connection.
    if (awareness.getLocalState()?.canvasDragPreviewBasisAck !== undefined)
      awareness.setLocalStateField('canvasDragPreviewBasisAck', undefined);

    // --- Outgoing throttle (sl-lv2w.9) ---
    // Accumulate changed client IDs between frames. A single rAF callback
    // encodes all accumulated changes into one awareness update message.
    const pendingOutgoing = new Set<number>();
    let cancelOutgoingFrame: (() => void) | null = null;

    const flushOutgoing = () => {
      cancelOutgoingFrame = null;
      if (pendingOutgoing.size === 0) {
        return;
      }
      const clients = Array.from(pendingOutgoing);
      pendingOutgoing.clear();

      const update = encodeAwarenessUpdate(awareness, clients);
      const compact = encodeCanvasDragAwareness(
        awareness,
        clients,
        false,
        update
      );
      const preview = awareness.getLocalState()?.canvasDragPreview;
      this.sync
        .update(
          {
            docId: awareness.doc.guid,
            bin: update,
            canvasDrag:
              preview && compact.byteLength < update.byteLength
                ? { bin: compact, dragId: preview.dragId }
                : undefined,
          },
          uniqueId
        )
        .catch(error => {
          console.error('update awareness error', error);
        });
    };

    const handleAwarenessUpdate = (
      changes: AwarenessChanges,
      origin: string
    ) => {
      if (origin === uniqueId) {
        return;
      }

      // Only the own state is sent. Every client reports itself to the server,
      // so a change of someone else's state never needs relaying. Relaying it
      // does harm: when the local 30 s timer drops a person whose renewals
      // this tab missed, the removal would reach every other viewer and hide
      // that person for all of them.
      const own = Object.values(changes).some(clientIds =>
        clientIds.includes(awareness.clientID)
      );
      if (!own) {
        return;
      }
      pendingOutgoing.add(awareness.clientID);

      // Schedule flush if not already scheduled
      if (!cancelOutgoingFrame) {
        cancelOutgoingFrame = scheduleFrame(flushOutgoing);
      }
    };

    awareness.on('change', handleAwarenessUpdate);

    // y-protocols renews the local state every 15 s and reports an unchanged
    // state as 'update' only. Receivers drop a state they have not heard of
    // for 30 s, so the renewal has to be sent like a change. States of other
    // clients are left to their own senders.
    const handleLocalRenewal = (changes: AwarenessChanges, origin: unknown) => {
      if (origin !== 'local') return;
      if (!changes.updated.includes(awareness.clientID)) return;
      pendingOutgoing.add(awareness.clientID);
      if (!cancelOutgoingFrame) {
        cancelOutgoingFrame = scheduleFrame(flushOutgoing);
      }
    };

    awareness.on('update', handleLocalRenewal);

    const hiddenClients = trackHiddenClients(awareness, uniqueId);

    // --- Incoming throttle (sl-lv2w.9) ---
    // Buffer incoming remote updates and apply them in the next animation frame
    // for smooth remote cursor rendering.
    let pendingIncoming: AwarenessRecord[] = [];
    let cancelIncomingFrame: (() => void) | null = null;

    const flushIncoming = () => {
      cancelIncomingFrame = null;
      const updates = pendingIncoming;
      pendingIncoming = [];

      for (const update of updates) {
        applyAwarenessUpdate(
          awareness,
          hiddenClients.withoutAwayClients(
            decodeCanvasDragAwareness(awareness, update.bin)
          ),
          uniqueId
        );
      }
      acknowledgeCanvasDragBases(awareness);
    };

    const handleSyncUpdate = (update: AwarenessRecord, origin?: string) => {
      if (origin === uniqueId) {
        // skip self update
        return;
      }

      pendingIncoming.push(update);

      if (!cancelIncomingFrame) {
        cancelIncomingFrame = scheduleFrame(flushIncoming);
      }
    };

    const handleSyncCollect = () => {
      return Promise.resolve({
        docId: awareness.doc.guid,
        bin: encodeAwarenessUpdate(awareness, [awareness.clientID]),
      });
    };
    const unsubscribe = this.sync.subscribeUpdate(
      awareness.doc.guid,
      handleSyncUpdate,
      handleSyncCollect
    );

    const cleanup = () => {
      awareness.off('change', handleAwarenessUpdate);
      awareness.off('update', handleLocalRenewal);
      hiddenClients.stop();
      // Flush any pending outgoing updates immediately on disconnect
      if (cancelOutgoingFrame) {
        cancelOutgoingFrame();
        cancelOutgoingFrame = null;
      }
      flushOutgoing();
      // Cancel pending incoming frame (no point applying after disconnect)
      if (cancelIncomingFrame) {
        cancelIncomingFrame();
        cancelIncomingFrame = null;
      }
      pendingIncoming = [];
      unsubscribe();
    };

    awareness.once('destroy', cleanup);

    return cleanup;
  }
}
